import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Check, Loader2, ArrowLeft, AlertCircle } from "lucide-react";
import axios from "axios";
import "../styles/legal.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo-cropped.png";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, confirm, success, error, already
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const emailParam = searchParams.get("email");
  const tokenParam = searchParams.get("token");

  useEffect(() => {
    const verifyLink = async () => {
      if (!emailParam || !tokenParam) {
        setStatus("error");
        setErrorMessage("Invalid unsubscribe link");
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/unsubscribe/verify?email=${encodeURIComponent(emailParam)}&token=${encodeURIComponent(tokenParam)}`
        );
        setEmail(response.data.email);
        
        if (response.data.already_unsubscribed) {
          setStatus("already");
        } else {
          setStatus("confirm");
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(error.response?.data?.detail || "Invalid unsubscribe link");
      }
    };

    verifyLink();
  }, [emailParam, tokenParam]);

  const handleUnsubscribe = async () => {
    setIsProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/unsubscribe`, {
        email: emailParam,
        token: tokenParam
      });
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Something went wrong");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="legal-page unsubscribe-page">
      <header className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <img src={LOGO_URL} alt="Thryvin'" className="legal-logo" />
        </div>
      </header>

      <main className="unsubscribe-content">
        <div className="container">
          <div className="unsubscribe-card">
            {status === "loading" && (
              <div className="unsubscribe-loading">
                <Loader2 size={48} className="spinner" />
                <p>Loading...</p>
              </div>
            )}

            {status === "confirm" && (
              <>
                <div className="unsubscribe-heart-icon">
                  <Heart size={48} />
                </div>
                <h1>Hey there! 👋</h1>
                <p className="unsubscribe-email">{email}</p>
                
                <div className="unsubscribe-friendly-message">
                  <p>We&apos;re sad to see you thinking about leaving!</p>
                  <p>Just so you know, if you unsubscribe you&apos;ll miss out on:</p>
                  <ul className="unsubscribe-perks">
                    <li>🔔 Being first to know when Thryvin&apos; launches</li>
                    <li>🎁 Your exclusive early supporter launch bonus</li>
                    <li>✨ All the exciting updates we&apos;ve got planned</li>
                  </ul>
                  <p className="unsubscribe-friendly-note">
                    No pressure though — we totally understand if our emails aren&apos;t your thing. 
                    Whatever you decide, we&apos;re grateful you gave us a shot! 💜
                  </p>
                </div>

                <div className="unsubscribe-actions">
                  <button 
                    className="unsubscribe-btn-cancel"
                    onClick={() => window.location.href = "/"}
                  >
                    Nevermind, keep me in!
                  </button>
                  <button 
                    className="unsubscribe-btn-confirm"
                    onClick={handleUnsubscribe}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <Loader2 size={20} className="spinner" />
                    ) : (
                      "Yes, unsubscribe me"
                    )}
                  </button>
                </div>
              </>
            )}

            {status === "success" && (
              <>
                <div className="unsubscribe-success-icon">
                  <Check size={48} />
                </div>
                <h1>You&apos;re unsubscribed</h1>
                <p>
                  We&apos;ve removed <strong>{email}</strong> from our list.
                </p>
                <p className="unsubscribe-note">
                  Thanks for checking us out — if you ever change your mind, you&apos;re always welcome back! 💜
                </p>
                <Link to="/" className="unsubscribe-home-link">
                  Back to Thryvin&apos;
                </Link>
              </>
            )}

            {status === "already" && (
              <>
                <div className="unsubscribe-success-icon">
                  <Check size={48} />
                </div>
                <h1>Already unsubscribed</h1>
                <p>
                  Looks like <strong>{email}</strong> is already off our list.
                </p>
                <p className="unsubscribe-note">
                  No worries — you won&apos;t receive any more emails from us.
                </p>
                <Link to="/" className="unsubscribe-home-link">
                  Back to Thryvin&apos;
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <div className="unsubscribe-error-icon">
                  <AlertCircle size={48} />
                </div>
                <h1>Oops!</h1>
                <p>{errorMessage}</p>
                <Link to="/" className="unsubscribe-home-link">
                  Back to Thryvin&apos;
                </Link>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <p>© {new Date().getFullYear()} Thryvin&apos;. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Unsubscribe;
